import type { ComponentProps } from 'react'
import { FormattedMessage, FormattedNumber } from 'react-intl'

import { cn } from '@gto/ui/cn'
import { Divider } from '@gto/ui/divider'
import { ArrowRightIcon } from '@gto/ui/icons/outline'
import { Table } from '@gto/ui/table'
import { Typography } from '@gto/ui/typography'

import type { TierStructureItem } from '@/api/gen'
import { safeNumber } from '@/utils/helpers'

export type EdgeTiersLegendProps = {
  tiers?: TierStructureItem[] | null
  currentTier?: number | null
}

// This legend is the one table that keeps MUI's default cell look: full
// padding and a divider under every head/body cell (the footer opts out).
const LegendCell = ({ className, ...props }: ComponentProps<typeof Table.Cell>) => (
  <Table.Cell
    className={cn('border-b border-divider', className)}
    padding="default"
    {...props}
  />
)

export const EdgeTiersLegend = ({ tiers = [], currentTier }: EdgeTiersLegendProps) => {
  if (tiers === null || tiers.length === 0) {
    return null
  }
  const userTier = tiers.find((t) => t.tier === currentTier)

  const getTierRange = (tier: TierStructureItem) => {
    if ((tier.threshold_min || tier.threshold_min === 0) && tier.threshold_max !== null) {
      return (
        <>
          <FormattedNumber value={tier.threshold_min} /> {' - '}
          <FormattedNumber value={tier.threshold_max} />
        </>
      )
    }

    if (tier.threshold_min && tier.threshold_max === null) {
      return (
        <>
          <FormattedNumber value={tier.threshold_min} />+
        </>
      )
    }
  }

  return (
    <div>
      <Typography
        className="p-4 text-center"
        variant="h6"
      >
        <FormattedMessage id="rewards.earnings.table.edgeMultiplierTiers" />
      </Typography>
      <Divider />

      <Table.Container>
        <Table.Root aria-label="edge tiers legend">
          <Table.Head>
            <Table.Row>
              <LegendCell>
                <Typography variant="subtitle2">
                  <FormattedMessage id="rewards.earnings.table.monthlyGGR" />
                </Typography>
              </LegendCell>
              <LegendCell>
                <Typography variant="subtitle2">
                  <FormattedMessage id="rewards.earnings.table.edgeMultiplier" />
                </Typography>
              </LegendCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {tiers.map((el) => {
              // Cells inherit the row's text color — no per-cell override needed.
              return (
                <Table.Row
                  key={el.tier}
                  className={
                    currentTier === el.tier
                      ? 'bg-primary-main text-primary-contrastText'
                      : 'text-text-primary'
                  }
                >
                  <LegendCell>
                    <Typography
                      className={cn(currentTier === el.tier && 'font-semibold')}
                      variant="body3"
                    >
                      {getTierRange(el)}
                    </Typography>
                  </LegendCell>
                  <LegendCell>
                    <Typography
                      className={cn(currentTier === el.tier && 'font-semibold')}
                      variant="body3"
                    >
                      <FormattedNumber value={safeNumber(el.multiplier)} />x
                    </Typography>
                  </LegendCell>
                </Table.Row>
              )
            })}
          </Table.Body>
          {userTier !== undefined && (
            <Table.Footer>
              <Table.Row>
                <Table.Cell
                  colSpan={2}
                  padding="default"
                >
                  <div className="flex w-full items-center justify-between gap-4">
                    <Typography
                      className="font-semibold"
                      color="text.primary"
                      variant="subtitle2"
                    >
                      <FormattedMessage id="rewards.earnings.table.yourTier" />
                    </Typography>

                    <div className="flex items-center gap-2">
                      <Typography
                        color="text.primary"
                        variant="subtitle2"
                      >
                        {getTierRange(userTier)}
                      </Typography>
                      <div className="flex items-center justify-center text-text-ghost">
                        <ArrowRightIcon width={20} />
                      </div>
                      <Typography
                        color="text.primary"
                        variant="subtitle2"
                      >
                        {' '}
                        <FormattedNumber value={safeNumber(userTier.multiplier)} />x
                      </Typography>
                    </div>
                  </div>
                </Table.Cell>
              </Table.Row>
            </Table.Footer>
          )}
        </Table.Root>
      </Table.Container>
    </div>
  )
}
